import clientService from '../apiClient';
import IconData from '../components/IconsData';
import { Notification } from '../components/Notification';
import { colorsCollection, REFRESH_TOKEN } from '../constants/common';
import { USER_LOGOUT } from './apiEndpoints';
import { CMS_USER_ROLES } from './constants';
import { deleteLocalStorage, getToken, removeCookie } from './cookie';

export const changeArrayForMenuItems = (list) => {
  return list.map((item) => [...item?.folders, ...item?.files]);
};

export const updateFilesInRecursiveFolder = (foldersList, targetFolder) => {
  if (!Array.isArray(foldersList)) return [];

  return foldersList.map((folder) => {
    // Convert 'folders' to 'children' if present
    if (folder.folders) {
      folder.children = [...folder.folders];
      delete folder.folders;
    }

    if (folder.children?.length > 0) {
      // If the folder matches the targetFolder id, update the children with the new files
      if (folder.id === targetFolder.id) {
        const updatedChildren = [
          ...folder.children.filter((item) => 'parent_id_id' in item),
          ...targetFolder.files.filter((item) => item.status !== fileStatus.REMOVED),
        ];

        // Sort the children based on the sidebar_info in meta_info
        const sortedChildren = sortFilesBySidebarInfo(updatedChildren, targetFolder.meta_info?.sidebar_info);

        return {
          ...folder,
          meta_info: targetFolder.meta_info || folder.meta_info, // Update meta_info
          children: sortedChildren,
        };
      } else {
        // Recurse into children folders
        return {
          ...folder,
          children: updateFilesInRecursiveFolder(folder.children, targetFolder),
        };
      }
    } else {
      // If no children and folder matches the targetFolder id, update children with the new files
      if (folder.id === targetFolder.id) {
        const sortedChildren = sortFilesBySidebarInfo(
          targetFolder.files.filter((item) => item.status !== fileStatus.REMOVED),
          targetFolder.meta_info?.sidebar_info
        );

        return {
          ...folder,
          meta_info: targetFolder.meta_info || folder.meta_info, // Update meta_info
          children: sortedChildren,
        };
      } else {
        return folder;
      }
    }
  });
};

// Helper function to sort files by sidebar_info
const sortFilesBySidebarInfo = (children, sidebarInfo) => {
  if (!sidebarInfo) return children;

  return children.sort((a, b) => {
    const posA = sidebarInfo[a.id];
    const posB = sidebarInfo[b.id];

    // If both positions are present, sort by them
    if (posA !== undefined && posB !== undefined) {
      return posA - posB;
    }

    // If one of the positions is missing, prioritize the one with the position
    if (posA !== undefined) return -1;
    if (posB !== undefined) return 1;

    // If neither position is present, maintain their original order
    return 0;
  });
};

export const findFileInRecursiveFolder = (foldersList, fileId) => {
  for (let folder of foldersList) {
    if (folder?.id === fileId) {
      return folder;
    }
    if (folder?.children && folder?.children?.length > 0) {
      const result = findFileInRecursiveFolder(folder?.children, fileId);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export const saveFileRecursively = (foldersList, targetFile) => {
  return foldersList?.map((folder) => {
    if (folder?.children?.length > 0) {
      if (folder?.id === targetFile?.id) {
        return targetFile;
      } else {
        return {
          ...folder,
          children: saveFileRecursively(folder?.children, targetFile),
        };
      }
    } else {
      if (folder?.id === targetFile?.id) {
        return targetFile;
      } else {
        return folder;
      }
    }
  });
};
export function removeItemById(folderList, idToRemove) {
  function deepCopy(obj) {
    return JSON.parse(JSON.stringify(obj));
  }
  function removeItem(items, id) {
    return items?.reduce((result, item) => {
      if (item.id !== id) {
        if (item.children) {
          // Recursively search in the children array
          item.children = removeItem(item.children, id);
        }
        result.push(item);
      }
      return result;
    }, []);
  }

  // Create a deep copy of the folderList
  const folderListCopy = deepCopy(folderList);

  // Remove the item from the deep copy and return the result
  return removeItem(folderListCopy, idToRemove);
}

export function findFileInTargetFolder(workspaceTree, targetFolderId, fileId) {
  // Helper function to find the target folder recursively
  function findTargetFolder(items, targetId) {
    for (const item of items) {
      // If the item matches the target folder ID, return it
      if (item.id === targetId && item.children) {
        return item;
      }

      // Recursively search in children
      if (item.children) {
        const found = findTargetFolder(item.children, targetId);
        if (found) {
          return found;
        }
      }
    }

    return null; // Return null if the target folder is not found
  }

  // Helper function to check if the file exists in the target folder
  function isFileInFolder(folder, fileId) {
    for (const item of folder.children) {
      // Check if this item is a file with the given file ID
      if (item.id === fileId && !item.children) {
        return item;
      }

      // Recursively check in nested folders
      if (item.children) {
        const found = isFileInFolder(item, fileId);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }
  const targetFolder = findTargetFolder(workspaceTree, targetFolderId);
  if (!targetFolder) {
    return null;
  }
  return isFileInFolder(targetFolder, fileId);
}

export const newmoveFileToNewFolder = (foldersList, targetFile, newFolderId) => {
  const findAndRemoveFile = (folders, fileId) => {
    return folders.reduce(
      (acc, folder) => {
        if (folder?.children) {
          const [removedFile, remainingChildren] = findAndRemoveFile(folder.children, fileId);
          if (removedFile) {
            return [removedFile, [...acc, { ...folder, children: remainingChildren }]];
          } else {
            return [null, [...acc, folder]];
          }
        }
        return folder.id === fileId ? [folder, acc] : [null, [...acc, folder]];
      },
      [null, []]
    );
  };

  const addFileToNewFolder = (folders, file, folderId) => {
    return folders.map((folder) => {
      if (folder.id === folderId) {
        return { ...folder, children: [...folder.children, file] };
      }
      if (folder?.children) {
        return { ...folder, children: addFileToNewFolder(folder.children, file, folderId) };
      }
      return folder;
    });
  };

  // Step 1: Find and remove the file from its original position
  const [fileToMove, updatedFoldersList] = findAndRemoveFile(foldersList, targetFile.id);
  if (!fileToMove) {
    throw new Error('File not found');
  }

  // Step 2: Add the file to the new folder or root
  if (newFolderId) {
    return addFileToNewFolder(updatedFoldersList, fileToMove, newFolderId);
  } else {
    // Add the file to the root level if newFolderId is null or undefined
    return [...updatedFoldersList, fileToMove];
  }
};

export const createFileRecursively = (foldersList, targetFile) => {
  if (!Array.isArray(foldersList)) return [];

  // if (targetFile.folder_id === null) return [...foldersList, targetFile];
  if (targetFile?.folder_id === null) return [...foldersList, targetFile];
  return foldersList.map((folder) => {
    if (folder.id === targetFile.folder_id) {
      return {
        ...folder,
        children: [...(folder.children || []), targetFile],
      };
    } else if (folder.children) {
      return {
        ...folder,
        children: createFileRecursively(folder.children, targetFile),
      };
    } else {
      return folder;
    }
  });
};

export const createFolderRecursively = (foldersList, targetFolder) => {
  if (!Array.isArray(foldersList)) return [];

  return foldersList.map((folder) => {
    if (folder.id === targetFolder.parent_id_id) {
      return {
        ...folder,
        children: [...(folder.children || []), targetFolder],
      };
    } else if (folder.children) {
      return {
        ...folder,
        children: createFolderRecursively(folder.children, targetFolder),
      };
    } else {
      return folder;
    }
  });
};
export const convertPostmanToOpenApi = async (fileContent) => {
  try {
    const response = await clientService.post(
      `/collection/add_collection`,
      {
        content: fileContent,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response;
  } catch (error) {
    Notification.error('Error converting and uploading file. Please try again.');
  }
};
export const updateFolderRecursively = (foldersList, targetFolder) => {
  if (!Array.isArray(foldersList)) return [];

  return foldersList?.map((folder) => {
    if (folder?.id === targetFolder?.id) {
      return {
        ...folder,
        ...targetFolder,
        children: folder.children ? [...folder.children] : [],
      };
    }
    if (folder.children) {
      return {
        ...folder,
        children: updateFolderRecursively(folder.children, targetFolder),
      };
    }

    return folder;
  });
};
export function sortFilesBySidebarPosition(data) {
  const sidebarInfo = data?.meta_info && data?.meta_info?.sidebar_info ? data?.meta_info?.sidebar_info : {};

  // Sort the files array
  const sortedFiles = data?.files?.sort((a, b) => {
    const positionA = sidebarInfo[a.id] !== undefined ? sidebarInfo[a.id] : Infinity;
    const positionB = sidebarInfo[b.id] !== undefined ? sidebarInfo[b.id] : Infinity;
    return positionA - positionB;
  });

  // Return a new object with the sorted files
  return {
    ...data,
    files: sortedFiles,
  };
}

export function moveFileToNewFolder(data, fileToMove) {
  // Deep clone the data to avoid mutating the original
  const newData = JSON.parse(JSON.stringify(data));

  // Function to remove the file from its current location
  function removeFile(items) {
    for (let i = 0; i < items.length; i++) {
      if (items[i].children) {
        items[i].children = items[i].children.filter((child) => child.id !== fileToMove.id);
        if (items[i].children.length === 0) {
          delete items[i].children;
        }
      }
    }
    return items;
  }

  // Function to add the file to its new location
  function addFile(items) {
    for (let i = 0; i < items.length; i++) {
      if (items[i].id === fileToMove.folder_id) {
        if (!items[i].children) {
          items[i].children = [];
        }
        items[i].children.push(fileToMove);
        return true;
      }
      if (items[i].children) {
        if (addFile(items[i].children)) {
          return true;
        }
      }
    }
    return false;
  }

  // Remove the file from its current location
  newData = removeFile(newData);

  // Add the file to its new location
  addFile(newData);

  // Update sidebar_info if it exists
  // newData.forEach(folder => {
  //   if (folder.meta_info && folder.meta_info.sidebar_info) {
  //     if (folder.id === fileToMove.folder_id) {
  //       // Add to new folder's sidebar_info
  //       const maxPosition = Math.max(...Object.values(folder.meta_info.sidebar_info), 0);
  //       folder.meta_info.sidebar_info[fileToMove.id] = maxPosition + 1;
  //     } else {
  //       // Remove from old folder's sidebar_info
  //       delete folder.meta_info.sidebar_info[fileToMove.id];
  //     }
  //   }
  // });

  return newData;
}
export function getWorkspaceById(id, data) {
  for (const type in data) {
    const workspace = data?.[type]?.find((ws) => ws.id === id);

    if (workspace) {
      return workspace;
    }
  }

  return null;
}

export function moveFile(data, file) {
  let fileMoved = false;

  // Deep clone the data array to avoid mutating the original
  const clonedData = JSON.parse(JSON.stringify(data));

  function findAndMoveFile(folders, file) {
    for (let i = 0; i < folders.length; i++) {
      const folder = folders[i];

      if (folder.children) {
        // Find the file in the current folder's children
        const fileIndex = folder.children.findIndex((child) => child.id === file.id);

        if (fileIndex !== -1) {
          // Remove the file from the previous folder
          const [removedFile] = folder.children.splice(fileIndex, 1);

          // Update the file's folder_id to the new folder_id
          removedFile.folder_id = file.folder_id;

          // Find the new folder to move the file to
          const newFolder = findFolderById(clonedData, file.folder_id);

          if (newFolder) {
            if (!newFolder.children) {
              newFolder.children = [];
            }
            // Add the file to the new folder
            newFolder.children.push(removedFile);
          } else {
            // Handle the case where the new folder_id is not found
          }

          fileMoved = true;
          return;
        }

        // Recursively search in nested folders
        findAndMoveFile(folder.children, file);
      }
    }
  }

  function findFolderById(folders, folderId) {
    for (const folder of folders) {
      if (folder.id === folderId) {
        return folder;
      }
      if (folder.children) {
        const nestedFolder = findFolderById(folder.children, folderId);
        if (nestedFolder) {
          return nestedFolder;
        }
      }
    }
    return null;
  }

  findAndMoveFile(clonedData, file);

  if (!fileMoved) {
  }

  return clonedData;
}

export const getGitLinkForBitbucket = (url) => {
  return url.split('/').slice(0, 7).join('/');
};

export function generateRandomName() {
  const adjectives = [];

  const nouns = [];

  const verbs = [];

  function capitalizeFirstLetter(word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  const getRandomWord = (wordList) => wordList[Math.floor(Math.random() * wordList.length)];

  // const filename = `${capitalizeFirstLetter(getRandomWord(adjectives))} ${capitalizeFirstLetter(getRandomWord(nouns))} ${capitalizeFirstLetter(getRandomWord(verbs))}`;

  return '';
}

export const fileStatus = {
  PUBLISHED: 'PUBLISHED',
  DRAFT: 'DRAFT',
  UNPUBLISHED: 'UNPUBLISHED',
  REMOVED: 'REMOVED',
};

export function buildQueryString(params) {
  return Object.keys(params)
    .map((key) => `${key}=${params[key]}`)
    .join('&');
}

export const isFolder = (obj) => 'parent_id_id' in obj;
export const isOpenApiFile = (obj) => obj?.type === 'openapi_spec_file';

export const constructFolderAndFileMappings = (items, mp = {}) => {
  items?.forEach((item) => {
    const { children, draft, data, markdown_data, ...rest } = item;
    mp[item.id] = { ...rest };
    if (children && Array.isArray(children)) {
      if ('parent_id_id' in item) {
        // This is a folder, include immediate children with necessary information
        mp[item.id].children = children.map((child) => {
          const { children, draft, data, markdown_data, is_draft_published, ...childRest } = child;
          return {
            ...childRest,
          };
        });
      }

      constructFolderAndFileMappings(children, mp);
    }
  });
  return mp;
};

export const findBitbucketFileRecursively = (foldersList, fileId) => {
  for (let folder of foldersList) {
    if (folder?.path === fileId) {
      return folder;
    }
    if (folder?.children && folder?.children?.length > 0) {
      const result = findBitbucketFileRecursively(folder?.children, fileId);
      if (result) {
        return result;
      }
    }
  }
  return null;
};

export const mapUserPermission = (users) => {
  return users?.reduce((acc, user) => {
    acc[user?.email] = {
      is_organization_admin: user?.is_organization_admin,
      workspace_permission: user?.workspace_permission,
      file_permission: user?.file_permission || null,
      email: user?.email,
      username: user?.username,
    };
    return acc;
  }, {});
};
export const formattedDate = (timestamp) => {
  function addFiveHoursThirtyMinutes(timestamp) {
    const givenTime = new Date(timestamp);

    // Add 5 hours and 30 minutes
    givenTime.setHours(givenTime.getHours() + 5);
    givenTime.setMinutes(givenTime.getMinutes() + 30);

    return givenTime;
  }

  const currentTime = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));

  const diffInSeconds = Math.floor((currentTime - addFiveHoursThirtyMinutes(timestamp)) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'min' : 'mins'} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} ${diffInMonths === 1 ? 'month' : 'months'} ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
};

export const calculateReadTime = (text) => {
  if (!text) return 1;

  // Strip HTML tags and other formatting elements
  const plainText = text
    .replace(/<\/?[^>]+(>|$)/g, '') // Remove HTML tags
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();

  // Split the text into words
  const wordCount = plainText.split(/\s+/).length;

  // Average reading speed: 200 words per minute (adjustable based on audience)
  const wordsPerMinute = 200;

  // Calculate reading time
  const readingTimeInMinutes = wordCount / wordsPerMinute;

  // Round up to the nearest minute
  return Math.max(1, Math.ceil(readingTimeInMinutes));
};

export function convertDatetime(datetimeStr) {
  // Create a new Date object from the string
  const date = new Date(datetimeStr);

  // Options for formatting the date
  const options = { month: 'short', day: '2-digit', year: 'numeric' };

  // Format the date using toLocaleDateString
  return date.toLocaleDateString('en-US', options);
}
export const userLogout = async () => {
  try {
    const body = {
      refresh_token: getToken(REFRESH_TOKEN),
    };

    const res = await clientService.post(USER_LOGOUT, { refresh_token: getToken(REFRESH_TOKEN) });
    localStorage.removeItem('persist:user');
    deleteLocalStorage();
    removeCookie();
    window.location.href = '/login';
    return res;
  } catch (error) {
    localStorage.removeItem('persist:user');
    deleteLocalStorage();
    removeCookie();
    window.location.href = '/login';
  }
};

export const checkUserPermission = (role, email, userPermissionMap) => {
  const userPermissionMapCopy = JSON.parse(JSON.stringify(userPermissionMap));
  if (!userPermissionMap[email]) {
    return false;
  }

  const user = userPermissionMapCopy[email];
  if (user?.is_organization_admin) {
    return true;
  }

  if (role === CMS_USER_ROLES.WORKSPACE_ADMIN) {
    return user?.workspace_permission === 'ADMIN';
  }
  if (role === CMS_USER_ROLES.WORKSPACE_WRITE) {
    return user?.workspace_permission === 'ADMIN' || user?.workspace_permission === 'WRITE';
  }
  if (role === CMS_USER_ROLES.FILE_WRITE) {
    return (
      user?.workspace_permission === 'ADMIN' ||
      user?.workspace_permission === 'WRITE' ||
      user?.file_permission === 'WRITE'
    );
  }

  return false;
};

export const findFileIndexInTree = (data, fileId) => {
  let index = -1; // Default index if not found

  // Loop through the top-level items
  for (let i = 0; i < data.length; i++) {
    // Check if the current item is a file (doesn't have children)
    if (data[i].children && data[i].children.length > 0) {
      // If it's a folder, check its children for the file
      const childIndex = findFileIndexInTree(data[i].children, fileId);
      if (childIndex !== -1) {
        return childIndex; // Return the index found in children
      }
    } else if (data[i].id === fileId) {
      index = i; // Update index if the file is found
      break; // Exit loop early since we found the file
    }
  }

  return index; // Return the found index or -1
};

export const checkPermission = (workspace_id, roleName, permissionMap) => {
  if (permissionMap?.is_organization_admin) {
    return true;
  } else {
    if (permissionMap?.permissions) {
      for (const perm of permissionMap?.permissions) {
        if (
          perm?.workspace_id === null &&
          perm.permission === 'WRITE' &&
          roleName === (CMS_USER_ROLES.WORKSPACE_WRITE || CMS_USER_ROLES.WORKSPACE_ADMIN)
        ) {
          return true;
        }
        if (perm?.workspace_id === workspace_id) {
          if (
            roleName === CMS_USER_ROLES.WORKSPACE_WRITE &&
            (perm.permission === 'WRITE' || perm.permission === 'ADMIN')
          ) {
            return true;
          }
          if (roleName === CMS_USER_ROLES.WORKSPACE_ADMIN && perm.permission === 'ADMIN') {
            return true;
          }
        }
      }
    }
  }
  return false;
};

export const isObjectEmpty = (obj) => {
  return !obj || typeof obj !== 'object' || Object.keys(obj).length === 0;
};

export function countRootFolders(data) {
  let rootFolderCount = 0;

  // Loop through the data to count folders with parent_id_id as null (root level folders)
  data.forEach((item) => {
    if (item.parent_id_id === null) {
      rootFolderCount++;
    }
  });

  return rootFolderCount;
}
export const pickDefined = (data) => {
  const output = {};
  Object.keys(data).forEach((key) => {
    if (typeof data[key] !== 'object' && data[key]) {
      output[key] = data[key];
    } else {
      if (
        (typeof data[key] === 'object' && Object.keys(data).length > 0) ||
        (Array.isArray(data[key]) && data[key].length > 0)
      ) {
        output[key] = data[key];
      }
    }
  });
  return output;
};
export function hasWorkspacePermission(userDetails, workspaceId) {
  // Check if the permissions array exists
  if (userDetails.is_organization_admin) {
    return true;
  }
  if (!userDetails.permissions) {
    return false;
  }

  // Check for the workspace_id in the permissions array
  return userDetails.permissions.some((permission) => permission.workspace_id === workspaceId);
}
export const filterPermissionBasedContent = (contents, username, userPermissions, workspace_id) => {
  const output = contents.filter((content) => {
    if (content?.permission) {
      return checkPermission(workspace_id, content?.permission, userPermissions);
    }

    return true;
  });

  return output;
};
export function convertUTC(date) {
  const offset = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + offset);
  return istDate;
}

export function convertUTCtoIST(utcDateString) {
  const date = new Date(utcDateString);
  const offsetIST = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(date.getTime() + offsetIST);
  return istTime;
}

export const isValidJSON = (str) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export function removeHTMLTags(htmlString) {
  if (htmlString) return htmlString.replace(/<\/?[^>]+(>|$)/g, '');
  return htmlString;
}

export const isValidLink = (to) => {
  try {
    if (!to || typeof to !== 'string') return false;

    // Ensure 'to' is a relative path and does not contain dangerous patterns
    if (
      to.includes('http://') ||
      to.includes('https://') ||
      to.includes('//') ||
      to.includes('\\') ||
      to.includes('..')
    )
      return false;

    // Validate path structure (only alphanumeric, dashes, underscores, and slashes)
    return /^[a-zA-Z0-9-_\/]+$/.test(to);
  } catch (error) {
    return false;
  }
};

// Function to add a folder or file
export function addItem(state, newItem) {
  const isFile = 'folder_id' in newItem ? true : false;
  const parentId = isFile ? newItem.folder_id : newItem.parent_id_id;
  if (isFile) {
    const { data, draft, markdown_data, ...rest } = newItem;
    newItem = { ...rest };
  }
  // If no parent ID is provided, add to the root
  if (!parentId) {
    const newItemToAdd = {
      ...(isFile ? {} : { children: [] }), // Remove children for files
      ...newItem,
    };
    return [...state, newItemToAdd];
  }

  return state.map((item) => {
    if (item.id === parentId) {
      // Adding to the parent folder
      if (!item.children) item.children = [];
      const newItemToAdd = {
        ...(isFile ? {} : { children: [] }), // Remove children for files
        ...newItem,
      };
      return {
        ...item,
        children: [...item.children, newItemToAdd],
      };
    }
    // Recursively search in children
    if (item.children) {
      return {
        ...item,
        children: addItem(item.children, newItem),
      };
    }
    return item;
  });
}

// Function to remove a folder or file
export function removeItem(state, itemId) {
  return state
    .map((item) => {
      if (item.id === itemId) {
        return null; // Mark for removal
      }
      if (item.children) {
        return {
          ...item,
          children: removeItem(item.children, itemId),
        };
      }
      return item;
    })
    .filter((item) => item !== null); // Remove marked items
}

export function getItem(state, itemId) {
  /**
   * Recursively searches the folder tree to find the target item (file or folder).
   *
   * @param {Array} items - The current level of the folder structure.
   * @param {string} targetId - The ID of the item to find.
   * @returns {Object|null} - The found item (file or folder) or null if not found.
   */
  function findItem(items, targetId) {
    for (const item of items) {
      if (item.id === targetId) {
        return item;
      }
      if (item.children) {
        const result = findItem(item.children, targetId);
        if (result) {
          return result;
        }
      }
    }
    return null;
  }

  return findItem(state, itemId);
}

export function getCommentContent(content) {
  try {
    return JSON.parse(content);
  } catch (e) {
    return content;
  }
}

export const isEditorBoxEmpty = (content, setIsEditorEmpty) => {
  try {
    const data = content?.content?.find((item) => item?.content && item?.content?.length);
    setIsEditorEmpty?.(!data);
    return !data;
  } catch (error) {
    console.error('Error checking if editor is empty:', error);
    setIsEditorEmpty?.(false); // Assume filled on error
    return false;
  }
};

export const getColorByName = (name) => {
  if (!name) return '#d9d9d9'; // Default gray color if name is empty
  const char = name[0].toUpperCase();
  const index = char.charCodeAt(0) - 65; // 'A' is 65 in ASCII
  return colorsCollection[index >= 0 && index < 26 ? index : 0]; // Fallback to first color if out of range
};

export const timeAgo = (timestamp) => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) {
    return `${seconds} second${seconds === 1 ? '' : 's'} ago`;
  }

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }

  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }

  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }

  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
};

export const findTemplateById = (templateId, templates) => {
  const _template = templates?.find((temp) => temp?.id?.toString() === templateId.toString());
  return _template;
};

export const getWorkSpaceTagColor = (type) => {
  // Using Ant Design's color palette for better theme compatibility
  const colorMap = {
    SERVICE: '#1890ff', // Bright blue - Ant Design's primary blue
    TEAM: '#52c41a', // Green - Ant Design's success color
    PROJECT: '#722ed1', // Purple - Ant Design's purple[7]
    OTHERS: '#faad14', // Gold - Ant Design's warning color
    DEFAULT: '#8c8c8c', // Gray - Ant Design's gray[6]
  };

  // Custom tag styles for better visibility
  const tagStyles = {
    backgroundColor: `${colorMap[type?.toUpperCase()] || colorMap.DEFAULT}20`, // 20% opacity background
    color: colorMap[type?.toUpperCase()] || colorMap.DEFAULT,
    border: `1px solid ${colorMap[type?.toUpperCase()] || colorMap.DEFAULT}40`, // 40% opacity border
    fontSize: '12px',
    padding: '0 8px',
    borderRadius: '4px',
  };

  return tagStyles;
};

export function debounce(func, delay) {
  let timeoutId;

  return function (...args) {
    // Clear the previous timeout if it exists
    clearTimeout(timeoutId);

    // Set a new timeout
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
}

export const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
};

/**
 * Finds valid workspace ID from userPermissions that are present in workspaceList
 * @param {Array} workspaceList - Array of workspace objects with id property
 * @param {Object} userPermissions - Object with permissions property containing workspace permissions
 * @returns {Array} - Array of valid workspace IDs
 */
export const findValidWorkspace = (workspaceList, userPermissions) => {
  // If either input is invalid, return empty array
  if (!workspaceList || !Array.isArray(workspaceList) || !userPermissions) {
    return [];
  }
  // Create a Set of valid workspace IDs from workspaceList for O(1) lookups
  const validWorkspaceIds = new Set(workspaceList.map((workspace) => workspace?.id));

  // If userPermissions has a "permissions" property that's an array
  if (userPermissions?.permissions && Array.isArray(userPermissions.permissions)) {
    // Use find instead of forEach to return the first matching workspace_id
    const validPermissionObj = userPermissions.permissions.find(
      (permissionObj) => permissionObj?.workspace_id && validWorkspaceIds.has(permissionObj.workspace_id)
    );

    // Return the workspace_id if a valid one was found
    if (validPermissionObj) {
      return validPermissionObj.workspace_id;
    }
  }
  return workspaceList?.find((item) => item?.type !== 'PRIVATE')?.id;
};

export function countSiblingsWithSameParent(newTreeData, index, workspaceId) {
  const targetParent = newTreeData[index]?.parent;
  if (targetParent === undefined) return 0;

  let count = targetParent === workspaceId ? 0 : 1;
  for (let i = index - 1; i >= 0; i--) {
    if (newTreeData[i]?.parent === targetParent) {
      count++;
    } else {
      break;
    }
  }

  return count;
}

export const validateEmail = (email) => {
  const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return re.test(String(email).toLowerCase());
};

/**
 * Recursively updates a file in the folder tree and optionally updates its parent folder's icon
 * if the file is the default for that folder.
 *
 * @param {Array} foldersList - The tree of folders and files.
 * @param {Object} updatedFile - The updated file object.
 * @returns {Array} - The new folder tree with applied updates.
 */
export const updateFileAndMaybeFolder = (foldersList, updatedFile) => {
  if (!Array.isArray(foldersList) || !updatedFile?.id) {
    console.warn('Invalid input to updateFileAndMaybeFolder');
    return foldersList;
  }

  const fileId = updatedFile.id;
  const folderId = updatedFile.folder_id ?? null;
  const newIcon = updatedFile.meta_info?.icon;

  const recursiveUpdate = (folders) => {
    return folders.map((folder) => {
      let updatedChildren = folder?.children?.length ? recursiveUpdate(folder.children) : folder.children;

      let updatedFolder = { ...folder, children: updatedChildren };

      // Always update the file if ID matches
      if (folder.id === fileId) {
        return updatedFile;
      }

      // Only update folder's icon if folder_id is valid and this folder is its parent
      if (folderId && folder.id === folderId && folder.meta_info?.default_file === fileId) {
        updatedFolder = {
          ...updatedFolder,
          meta_info: {
            ...updatedFolder.meta_info,
            icon: newIcon,
          },
        };
      }

      return updatedFolder;
    });
  };

  try {
    return recursiveUpdate(foldersList);
  } catch (error) {
    console.error('Error updating file and/or folder:', error);
    return foldersList; // Fallback to original data
  }
};
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
export const formatDateOnly = (dateString) => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

export function preloadDocumentTemplate(imageUrl) {
  if (document.querySelector(`link[rel="preload"][href="${imageUrl}"]`)) return;

  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = imageUrl;
  link.type = 'image/webp';
  link.setAttribute('fetchpriority', 'high');
  document.head.appendChild(link);
}
